/**
 * CollapsibleSection Component
 * Expandable group header with icon, title, count badge, and animated chevron.
 */

import { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

export interface CollapsibleSectionProps {
  title: string;
  count: number;
  icon?: ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  variant?: 'warning' | 'info' | 'neutral' | 'success';
  children: ReactNode;
}

interface VariantStyle {
  headerBg: string;
  headerBorder: string;
  headerColor: string;
  countBg: string;
  countColor: string;
}

const VARIANT_STYLES: Record<NonNullable<CollapsibleSectionProps['variant']>, VariantStyle> = {
  warning: {
    headerBg: theme.colors.status.warning.bg,           // #fffbeb
    headerBorder: theme.colors.status.warning.border,   // #fde68a
    headerColor: theme.colors.status.warning.text,      // #d97706 (was #92400e — updated to theme token)
    countBg: theme.colors.status.warning.bg,            // #fffbeb (was #fef3c7 — lighter, matches theme)
    countColor: theme.colors.status.warning.text,       // #d97706
  },
  info: {
    headerBg: theme.colors.status.info.bg,              // #f0fdfc
    headerBorder: theme.colors.status.info.border,      // #99f6e4
    headerColor: theme.colors.status.info.text,         // #2d7370
    // Teal-100 for count badge — not in theme; keeping hex
    countBg: '#ccfbf1',
    countColor: theme.colors.status.success.text,       // #16a34a (was #065f46 — closest theme token)
  },
  success: {
    headerBg: theme.colors.status.success.bg,           // #f0fdf4
    headerBorder: theme.colors.status.success.border,   // #bbf7d0
    headerColor: theme.colors.status.success.text,      // #16a34a (was #065f46 — updated to theme token)
    // Tailwind emerald-100 for count badge bg — slightly more saturated than success.bg
    countBg: '#d1fae5',
    countColor: theme.colors.status.success.text,       // #16a34a
  },
  neutral: {
    headerBg: theme.colors.backgrounds.page,            // #f9fafb
    headerBorder: theme.colors.borders.light,           // #e5e7eb
    headerColor: theme.colors.text.secondary,           // #4b5563 (was #374151 — closest theme token)
    countBg: theme.colors.backgrounds.gray,             // #f3f4f6
    // Light gray text — not in theme as a dedicated token; keeping hex
    countColor: '#6b7280',
  },
};

const SectionWrapper = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.backgrounds.white};
`;

const Header = styled.button<{
  $headerBg: string;
  $headerBorder: string;
  $headerColor: string;
}>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ $headerBg }) => $headerBg};
  border: none;
  border-bottom: 1px solid ${({ $headerBorder }) => $headerBorder};
  color: ${({ $headerColor }) => $headerColor};
  cursor: pointer;
  text-align: left;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    filter: brightness(0.97);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: -2px;
  }
`;

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const Title = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`;

const CountBadge = styled.span<{ $bg: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const ChevronWrapper = styled.span<{ $isExpanded: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  transition: transform ${({ theme }) => theme.transitions.base};
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(90deg)' : 'rotate(0deg)')};
`;

const ContentWrapper = styled.div<{ $isExpanded: boolean }>`
  overflow: hidden;
  transition: max-height ${({ theme }) => theme.transitions.slow};
  max-height: ${({ $isExpanded }) => ($isExpanded ? '9999px' : '0px')};
`;

export function CollapsibleSection({
  title,
  count,
  icon,
  isExpanded,
  onToggle,
  variant = 'neutral',
  children,
}: CollapsibleSectionProps) {
  const styles = VARIANT_STYLES[variant];
  const sectionId = `section-${title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <SectionWrapper>
      <Header
        $headerBg={styles.headerBg}
        $headerBorder={styles.headerBorder}
        $headerColor={styles.headerColor}
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={sectionId}
        type="button"
      >
        {icon && <IconSlot>{icon}</IconSlot>}
        <Title>{title}</Title>
        <CountBadge $bg={styles.countBg} $color={styles.countColor} aria-label={`${count} tasks`}>
          {count}
        </CountBadge>
        <ChevronWrapper $isExpanded={isExpanded} aria-hidden="true">
          <ChevronRight size={16} />
        </ChevronWrapper>
      </Header>
      <ContentWrapper id={sectionId} $isExpanded={isExpanded} role="region" aria-label={title}>
        {children}
      </ContentWrapper>
    </SectionWrapper>
  );
}

export default CollapsibleSection;
